import type { ContentHubSource } from "./config";
import type { ContentHubDateField, ContentHubFilter } from "./filter";

export interface ContentHubTextSearchInput {
  /** Free-text query from a widget header's search box. */
  query: string;
  /** backend_source(s) to scope the search to — the widget's content-hub
   *  source. Omit to let a caller pin the source another way (the headlines
   *  search pins `newsquawk-headlines` via `useContentHubSearch`'s pinnedSource). */
  sources?: ContentHubSource | readonly ContentHubSource[];
  /** Exact match: treat the whole query as one literal `verbatim` term
   *  (unstemmed, case-insensitive, multi-word = exact phrase) instead of
   *  OR-ing its stemmed words. A user toggle. */
  exactMatch?: boolean;
  /** How the query's terms combine. Default "or". */
  keywordsOp?: "and" | "or";
  /** Inclusive lower date bound (`gte`) — ISO-8601 string or epoch-ms. */
  dateFrom?: string | number;
  /** Inclusive upper date bound (`lte`) — ISO-8601 string or epoch-ms. */
  dateTo?: string | number;
  /** Date column to range-filter. Default "occurs_at". */
  dateField?: ContentHubDateField;
}

/**
 * Base search logic shared by every content-hub widget's header text search:
 * turn a header search box's inputs into a `ContentHubFilter`. This is the one
 * place a free-text query + a widget's `backend_source`(s) become a content-hub
 * `/v1/search` filter — each widget builds on top by passing its own source(s).
 *
 * The resulting filter still flows through the existing per-widget display
 * adapters (`useContentHubFirehoseSearch`, `useContentHubHeadlinesSearch`) and
 * `useContentHubSearch`, so header search is now the same pipeline the feed and
 * the funnel-filter panels already use — including live updates.
 *
 * Keyword semantics (see `buildTree`):
 *  - default: each word becomes an OR-ed term (single words collapse into one
 *    `text` leaf);
 *  - `exactMatch`: the whole query is a single verbatim (literal, unstemmed)
 *    leaf.
 *
 * An empty query yields a source-only filter, so callers gate the query on a
 * non-empty search term rather than fetching the whole feed.
 */
export function buildContentHubTextFilter({
  query,
  sources,
  exactMatch = false,
  keywordsOp = "or",
  dateFrom,
  dateTo,
  dateField,
}: ContentHubTextSearchInput): ContentHubFilter {
  const q = query.trim();
  const sourceList =
    sources === undefined
      ? undefined
      : typeof sources === "string"
        ? [sources]
        : [...sources];
  const keywords = q ? (exactMatch ? [q] : q.split(/\s+/)) : undefined;

  const hasFrom = dateFrom !== undefined && dateFrom !== "";
  const hasTo = dateTo !== undefined && dateTo !== "";
  const dateRange =
    hasFrom || hasTo
      ? { field: dateField, from: dateFrom, to: dateTo }
      : undefined;

  // Exact match compiles the query into a single verbatim leaf
  // (`{ text: [q], verbatim: true }`) — a literal, unstemmed, case-insensitive
  // match (multi-word = exact phrase). See `compileKeywords`.
  return {
    keywords,
    keywordsOp,
    keywordsExact: exactMatch,
    sources: sourceList,
    dateRange,
  };
}
